// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import OverflowMenu, { OverflowMenuPresentationProps } from "@conklin/MobiusOverrides/OverflowMenu";
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import { API_URL_CURRENT_FRAGMENT } from "@insite/client-framework/Services/ApiService";
import { ShareOptions } from "@insite/client-framework/Services/WishListService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getWishListLinesDataView } from "@insite/client-framework/Store/Data/WishListLines/WishListLinesSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import addLinesToCart from "@insite/client-framework/Store/Pages/Cart/Handlers/AddLinesToCart";
import addWishListToCart from "@insite/client-framework/Store/Pages/Cart/Handlers/AddWishListToCart";
import loadWishListLines from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/LoadWishListLines";
import updateLoadWishListLinesParameter from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/UpdateLoadWishListLinesParameter";
import translate from "@insite/client-framework/Translate";
import { CartLineCollectionModel, CartLineModel, WishListModel } from "@insite/client-framework/Types/ApiModels";
import MyListsDetailsPageTypeLink from "@insite/content-library/Components/MyListsDetailsPageTypeLink";
import WishListSharingStatus, {
    WishListSharingStatusStyles,
} from "@insite/content-library/Components/WishListSharingStatus";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Star from "@insite/mobius/Icons/Star";
import StarX from "@insite/mobius/Icons/StarX";
import Tag from "@insite/mobius/Icons/Tag";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { markForFocusLater } from "@insite/mobius/Overlay/helpers/focusManager";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Tooltip, { TooltipPresentationProps } from "@insite/mobius/Tooltip";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useContext } from "react";
import { connect, HandleThunkActionCreator, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState, props: OwnProps) => ({
    language: state.context.session.language,
    allowEditingOfWishLists: getSettingsCollection(state).wishListSettings.allowEditingOfWishLists,
    isUpdatingCart: state.context.isUpdatingCart,
    // XNG-Change
    pageLink: getPageLinkByPageType(state, "MyListsDetailsPage"),
    wishListLinesDataView: getWishListLinesDataView(state, state.pages.myListDetails.loadWishListLinesParameter),
    productInfosByWishListLineId: state.pages.myListDetails.productInfosByWishListLineId,
    wishListId: state.pages.myListDetails.wishListId,
});

// XNG-Change
const mapDispatchToProps = {
    addLinesToCart,
    updateLoadWishListLinesParameter,
    loadWishListLines,
};

interface OwnProps {
    wishList: WishListModel;
    extendedStyles?: WishListCardStyles;
    addWishListToCart: HandleThunkActionCreator<typeof addWishListToCart>;
    deleteWishList?: () => void;
    leaveWishList?: () => void;
    toggleFavorite?: () => void;
    toggleHide?: () => void;
}

type Props = ReturnType<typeof mapStateToProps> & OwnProps & ResolveThunks<typeof mapDispatchToProps>;

export interface WishListCardStyles {
    gridContainer?: GridContainerProps;
    wishListInfoGridItem?: GridItemProps;
    wishListInfoGridContainer?: GridContainerProps;
    basicInfoGridItem?: GridItemProps;
    basicInfoGridContainer?: GridContainerProps;
    detailLinkGridItem?: GridItemProps;
    descriptionGridItem?: GridItemProps;
    descriptionText?: TypographyProps;
    lastUpdatedGridItem?: GridItemProps;
    lastUpdatedText?: TypographyProps;
    extendedInfoGridItem?: GridItemProps;
    extendedInfoGridContainer?: GridContainerProps;
    productImagesGridItem?: GridItemProps;
    productImageLink?: LinkPresentationProps;
    productImage?: LazyImageProps;
    sharingStatusGridItem?: GridItemProps;
    sharingStatus?: WishListSharingStatusStyles;
    actionGridItem?: GridItemProps;
    actionGridMediumHidden?: HiddenProps;
    actionOverflowIcon?: IconPresentationProps;
    actionOverflowMenu?: OverflowMenuPresentationProps;
    actionAddToCartClickable?: ClickablePresentationProps;
    actionHideClickable?: ClickablePresentationProps;
    actionDeleteClickable?: ClickablePresentationProps;
    actionLeaveClickable?: ClickablePresentationProps;
    actionAddToCartButton?: ButtonPresentationProps;
    actionHideLink?: LinkPresentationProps;
    actionDeleteButton?: ButtonPresentationProps;
    actionLeaveButton?: ButtonPresentationProps;
    actionFavoriteClickable?: ClickablePresentationProps;
    basicIconGridContainer?: GridContainerProps;
    detailIconGridItem?: GridItemProps;
    starIcon?: IconPresentationProps;
    starXIcon?: IconPresentationProps;
    tagTooltip?: TooltipPresentationProps;
    // XNG-Changes
    container?: InjectableCss;
    descriptionWrapper?: InjectableCss;
    imagesWrapper?: InjectableCss;
    actionsWrapper?: InjectableCss;
    sharingStatusWrapper?: InjectableCss;
    detailIconWrapper?: InjectableCss;
    nameWrapper?: InjectableCss;
    favTagsWrapper?: InjectableCss;
    tagIcon?: IconPresentationProps;
    overflowMenu?: OverflowMenuPresentationProps;
    moreActions?: LinkPresentationProps;
}

export const wishListCardStyles: WishListCardStyles = {
    gridContainer: {
        gap: 5,
    },
    wishListInfoGridItem: {
        width: [10, 10, 8, 10, 10],
        align: "middle",
    },
    basicInfoGridItem: { width: [12, 12, 5, 4, 4] },
    basicInfoGridContainer: {
        gap: 10,
        css: css`
            margin-left: 10px;
        `,
    },
    detailLinkGridItem: { width: 12 },
    descriptionGridItem: { width: 12 },
    descriptionText: { ellipsis: true },
    lastUpdatedGridItem: { width: 12 },
    extendedInfoGridItem: {
        width: [12, 12, 7, 8, 8],
        align: "middle",
    },
    productImageLink: {
        css: css`
            margin-right: 5px;
        `,
    },
    productImagesGridItem: {
        width: [12, 12, 12, 8, 8],
        css: css`
            flex-wrap: wrap;
        `,
    },
    productImage: {
        css: css`
            max-width: 75px;
            img {
                height: 100%;
            }
        `,
        errorTypographyProps: {
            size: 11,
            css: css`
                max-height: 65px;
            `,
        },
    },
    sharingStatusGridItem: { width: [12, 12, 12, 4, 4] },
    actionGridItem: {
        width: [2, 2, 4, 2, 2],
        align: "top",
        css: css`
            justify-content: flex-end;
        `,
    },
    actionGridMediumHidden: {
        css: css`
            width: 100%;
        `,
    },
    actionAddToCartButton: {
        color: "secondary",
        css: css`
            white-space: nowrap;
            width: 100%;
            padding: 0 10px;
        `, // keep text from wrapping
    },
    actionHideLink: {
        css: css`
            display: block;
            margin: 10px auto 0 auto;
        `,
    },
    actionDeleteButton: {
        variant: "secondary",
        css: css`
            width: 100%;
            margin-top: 10px;
        `,
    },
    actionLeaveButton: {
        variant: "secondary",
        css: css`
            width: 100%;
            margin-top: 10px;
        `,
    },
    sharingStatus: {
        privateTooltip: {
            cssOverrides: {
                tooltipContainer: css`
                    transform: translateX(-33%);
                `,
                tooltipArrow: css`
                    left: 33%;
                `,
            },
        },
    },
    basicIconGridContainer: {
        gap: 10,
        css: css`
            width: 15px;
            flex-shrink: 0;
        `,
        offsetCss: css`
            flex-direction: column;
        `,
    },
    actionFavoriteClickable: {
        css: css`
            width: 15px;
        `,
    },
    starIcon: {
        src: Star,
        size: 15,
    },
    starXIcon: {
        src: StarX,
        size: 15,
    },
    tagTooltip: {
        iconProps: {
            src: Tag,
            size: 15,
            css: css`
                margin: 4px 0;
            `,
        },
    },
};

const WishListCard = ({
    language,
    allowEditingOfWishLists,
    isUpdatingCart,
    extendedStyles,
    wishList,
    addWishListToCart,
    deleteWishList,
    leaveWishList,
    toggleFavorite,
    toggleHide,
    // XNG-Change
    pageLink,
    addLinesToCart,
    wishListLinesDataView,
    productInfosByWishListLineId,
    wishListId,
    updateLoadWishListLinesParameter,
    loadWishListLines,
}: Props) => {
    const toasterContext = useContext(ToasterContext);
    const isLoadingAnyList = useAppSelector(state => Object.keys(state.data.wishLists.isLoading).length > 0);

    // XNG-Changes
    const [addingToCart, setAddingToCart] = React.useState(false);
    const dispatch = useAppDispatch();

    const clickAddToCartHandler = (e: any) => {
        e.preventDefault();
        // XNG-Changes
        setAddingToCart(true);
        dispatch({
            type: "Pages/MyListDetails/SetWishListId",
            wishListId: wishList.id,
        });
        updateLoadWishListLinesParameter({ wishListId: wishList.id });
        loadWishListLines({ skipResetWishLists: true } as any);
    };

    // XNG-Changes
    React.useEffect(() => {
        if (
            wishList.id === wishListId &&
            wishListLinesDataView?.value &&
            productInfosByWishListLineId &&
            Object.keys(productInfosByWishListLineId).length !== 0 &&
            addingToCart
        ) {
            const linesToAdd = wishListLinesDataView.value.filter(
                o => !o.quoteRequired && productInfosByWishListLineId[o.id]?.pricing?.unitNetPrice !== 0,
            );
            const notAllAddedToCart = linesToAdd.length !== wishListLinesDataView.value.length;
            if (linesToAdd.length === 0) {
                toasterContext.addToast({ body: siteMessage("Cart_InvalidPrice"), messageType: "danger" });
                return;
            }
            addLinesToCart({
                apiParameter: {
                    cartId: API_URL_CURRENT_FRAGMENT,
                    cartLineCollection: {
                        cartLines: linesToAdd.map(o => o as any as CartLineModel),
                    } as CartLineCollectionModel,
                },
                onSuccess: () => {
                    setAddingToCart(false);
                    onAddAllToCartSuccess({
                        notAllAddedToCart,
                    } as CartLineCollectionModel);
                },
                onComplete(resultProps) {
                    if (resultProps.apiResult) {
                        this.onSuccess?.();
                    }
                },
            });
        }
    }, [productInfosByWishListLineId]);

    const onAddAllToCartSuccess = (result: CartLineCollectionModel) => {
        toasterContext.addToast({
            body: result.notAllAddedToCart
                ? siteMessage("Lists_Items_Not_All_Added_To_Cart")
                : translate("Project Added to Cart"),
            messageType: "success",
        });
    };

    const updatedOnDisplay = getLocalizedDateTime({
        dateTime: new Date(wishList.updatedOn),
        language,
        options: {
            // XNG-Changes
            year: "2-digit",
            month: "numeric",
            day: "numeric",
        },
    });
    let lastUpdatedDisplay = `${translate("Updated")} ${updatedOnDisplay}`;
    if (wishList.updatedByDisplayName && !wishList.isAutogenerated) {
        lastUpdatedDisplay += ` ${translate("by")} ${wishList.updatedByDisplayName}`;
    }

    const canAddToCart = wishList.canAddToCart && !!wishList.wishListLinesCount && wishList.wishListLinesCount > 0;
    const addToCartLabel = translate("Add Project to Cart");
    const toggleHideButtonText = wishList.isHidden ? translate("Unhide Project") : translate("Hide Project");
    const toggleFavoriteLabel = wishList.isFavorite
        ? translate("Remove from favorites")
        : translate("Add to favorites");
    const deleteLabel = translate("Delete Project");
    const leaveLabel = translate("Leave Project");
    const [styles] = React.useState(() => mergeToNew(wishListCardStyles, extendedStyles));

    return (
        <StyledWrapper {...styles.container} data-test-selector="wishListCard">
            <StyledWrapper {...styles.descriptionWrapper} data-test-selector={`${wishList.name}`}>
                <StyledWrapper>
                    <StyledWrapper {...styles.nameWrapper}>
                        <MyListsDetailsPageTypeLink
                            title={wishList.name}
                            wishListId={wishList.id}
                            testSelector="wishListCardName"
                        />
                    </StyledWrapper>

                    <Typography {...styles.lastUpdatedText} data-test-selector="wishListCardLastUpdated">
                        {lastUpdatedDisplay}
                    </Typography>
                    {wishList.description && (
                        <Typography {...styles.descriptionText} data-test-selector="wishListCardDescription">
                            {wishList.description}
                        </Typography>
                    )}
                    <StyledWrapper {...styles.favTagsWrapper}>
                        {!wishList.isAutogenerated && (
                            <StyledWrapper {...styles.detailIconWrapper}>
                                <Clickable
                                    {...styles.actionFavoriteClickable}
                                    onClick={toggleFavorite}
                                    data-test-selector={`myListsFavorite_${wishList.id}`}
                                    disabled={isLoadingAnyList}
                                    aria-label={toggleFavoriteLabel}
                                >
                                    {wishList.isFavorite && (
                                        <>
                                            <Icon
                                                {...styles.starXIcon}
                                                data-test-selector={`myListsFavoriteStarXIcon_${wishList.id}`}
                                            />
                                            {/* XNG-Changes */}
                                            {translate("Unfavorite")}
                                        </>
                                    )}
                                    {!wishList.isFavorite && (
                                        <>
                                            <Icon
                                                {...styles.starIcon}
                                                data-test-selector={`myListsFavoriteStarIcon_${wishList.id}`}
                                            />
                                            {/* XNG-Changes */}
                                            {translate("Favorite")}
                                        </>
                                    )}
                                </Clickable>
                            </StyledWrapper>
                        )}
                        {wishList.wishListTags && wishList.wishListTags.length > 0 && (
                            <StyledWrapper
                                {...styles.detailIconWrapper}
                                data-test-selector={`myListsTags_${wishList.id}`}
                            >
                                <Tooltip
                                    {...styles.tagTooltip}
                                    // XNG-Changes
                                    text={`${wishList.wishListTags.map(o => o.tag).join(", ")}`}
                                    triggerComponent={
                                        <>
                                            <Icon
                                                {...styles.tagIcon}
                                                data-test-selector={`myListsFavoriteStarIcon_${wishList.id}`}
                                            />
                                            <Typography>{translate("View Tags")}</Typography>
                                        </>
                                    }
                                />
                            </StyledWrapper>
                        )}
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
            <StyledWrapper {...styles.imagesWrapper} data-test-selector="productImagesGridItem">
                {wishList.wishListLineCollection &&
                    wishList.wishListLineCollection.length > 0 &&
                    wishList.wishListLineCollection.slice(0, 3).map(line => (
                        <Link
                            key={line.id.toString()}
                            {...styles.productImageLink}
                            href={pageLink ? `${pageLink?.url}?id=${wishList.id}` : ""}
                        >
                            <LazyImage
                                {...styles.productImage}
                                src={line.smallImagePath}
                                altText={line.altText}
                                title={line.altText}
                            />
                        </Link>
                    ))}
            </StyledWrapper>
            <StyledWrapper {...styles.sharingStatusWrapper}>
                <WishListSharingStatus extendedStyles={styles.sharingStatus} wishList={wishList} />
            </StyledWrapper>
            <StyledWrapper {...styles.actionsWrapper}>
                {/* <Hidden above="sm">
                    <OverflowMenu position="end" {...styles.actionOverflowMenu}>
                        <Clickable
                            {...styles.actionAddToCartClickable}
                            disabled={!canAddToCart || isUpdatingCart}
                            onClick={clickAddToCartHandler}
                        >
                            {addToCartLabel}
                        </Clickable>
                        {!wishList.isAutogenerated && (
                            <Clickable {...styles.actionHideClickable} disabled={isLoadingAnyList} onClick={toggleHide}>
                                {toggleHideButtonText}
                            </Clickable>
                        )}
                        {allowEditingOfWishLists &&
                            !!deleteWishList &&
                            !wishList.isSharedList &&
                            !wishList.isAutogenerated && (
                                <Clickable {...styles.actionDeleteClickable} onClick={deleteWishList}>
                                    {deleteLabel}
                                </Clickable>
                            )}
                        {allowEditingOfWishLists &&
                            !!leaveWishList &&
                            wishList.isSharedList &&
                            wishList.shareOption !== ShareOptions.AllCustomerUsers && (
                                <Clickable {...styles.actionLeaveClickable} onClick={leaveWishList}>
                                    {leaveLabel}
                                </Clickable>
                            )}
                    </OverflowMenu>
                </Hidden> */}
                <StyledWrapper {...styles.actionGridMediumHidden}>
                    <Button
                        {...styles.actionAddToCartButton}
                        // XNG-Changes
                        disabled={!canAddToCart || isUpdatingCart || addingToCart}
                        data-test-selector={`wishListAddToCartButton_${wishList.id}`}
                        onClick={clickAddToCartHandler}
                    >
                        {addToCartLabel}
                    </Button>
                    {/* Xng-Changes */}
                    {!wishList.isAutogenerated && (
                        <OverflowMenu
                            key="overflowMenu"
                            onOpen={markForFocusLater}
                            {...styles.overflowMenu}
                            popoverTrigger={
                                // Xng-Change: aria-label
                                <Link {...styles.moreActions} aria-label="More Actions">
                                    {translate("More Actions")}
                                </Link>
                            }
                            transitionDuration="short"
                            popoverPositionFunction={() => ({
                                position: "absolute",
                                top: "20px",
                                left: "0",
                            })}
                        >
                            <Clickable {...styles.actionHideClickable} disabled={isLoadingAnyList} onClick={toggleHide}>
                                {toggleHideButtonText}
                            </Clickable>
                            {allowEditingOfWishLists && !!deleteWishList && !wishList.isSharedList && (
                                <Clickable {...styles.actionDeleteClickable} onClick={deleteWishList}>
                                    {deleteLabel}
                                </Clickable>
                            )}
                            {allowEditingOfWishLists &&
                                !!leaveWishList &&
                                wishList.isSharedList &&
                                wishList.shareOption !== ShareOptions.AllCustomerUsers && (
                                    <Clickable {...styles.actionLeaveClickable} onClick={leaveWishList}>
                                        {leaveLabel}
                                    </Clickable>
                                )}
                        </OverflowMenu>
                    )}
                    {/* {!wishList.isAutogenerated && (
                        <Link
                            {...styles.actionHideLink}
                            onClick={toggleHide}
                            data-test-selector={`myListsHide_${wishList.id}`}
                            disabled={isLoadingAnyList}
                        >
                            {toggleHideButtonText}
                        </Link>
                    )}
                    {allowEditingOfWishLists &&
                        !!deleteWishList &&
                        !wishList.isSharedList &&
                        !wishList.isAutogenerated && (
                            <Button
                                {...styles.actionDeleteButton}
                                onClick={deleteWishList}
                                data-test-selector={`wishListCardDeleteButton_${wishList.id}`}
                            >
                                {deleteLabel}
                            </Button>
                        )}
                    {allowEditingOfWishLists &&
                        !!leaveWishList &&
                        wishList.isSharedList &&
                        wishList.shareOption !== ShareOptions.AllCustomerUsers && (
                            <Button
                                {...styles.actionLeaveButton}
                                onClick={leaveWishList}
                                data-test-selector={`wishListCardLeaveButton_${wishList.id}`}
                            >
                                {leaveLabel}
                            </Button>
                        )} */}
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(WishListCard);
